import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'

type ApprovalItem = {
  change_id: number
  release_id: number
  release_version: string
  dataset: string
  operacao: 'INSERT' | 'UPDATE' | 'DELETE'
  record_key: string
  dados_anteriores: Record<string, unknown> | null
  dados_novos: Record<string, unknown> | null
  submitted_at: string | null
  requested_by: string
}

const DATASET_LABELS: Record<string, string> = {
  empreendimentos: 'Empreendimentos',
  suite_help: 'Suite Help',
  qualificacoes: 'Qualificações',
  acionamentos: 'Acionamentos',
  servicos: 'Tabela de Serviços',
  irregularidades: 'Irregularidades',
  config_tarifas: 'Configuração · Tabela Tarifária',
  config_negociacao: 'Configuração · Regras de Negociação',
  config_vencimentos_grupo: 'Configuração · Vencimentos por Grupo',
}

const FIELD_LABELS: Record<string, string> = {
  empreendimento: 'Empreendimento',
  primeira_cobranca_agua: '1ª cobrança Água',
  primeira_cobranca_esgoto: '1ª cobrança Esgoto',
  segunda_cobranca_agua: '2ª cobrança Água',
  segunda_cobranca_esgoto: '2ª cobrança Esgoto',
  ramal_rede: 'Ramal / Rede',
  tipo_loteamento: 'Tipo de loteamento',
  codigo: 'Código',
  ordem_servico: 'Ordem de Serviço',
  assunto: 'Assunto',
  custo: 'Custo',
  prazo: 'Prazo',
  quem_pode_solicitar: 'Quem pode solicitar',
  canal_solicitacao: 'Canal de solicitação',
  quando_abrir: 'Quando abrir',
  sugestao_texto: 'Sugestão de texto',
  observacao_ia: 'Observação IA',
  palavras_chave: 'Palavras-chave',
  effective_from: 'Vigência',
  title: 'Identificação',
  payload: 'Regras e valores',
  config_key: 'Tipo de configuração',
}

function fieldLabel(field: string) {
  return FIELD_LABELS[field] ?? field.replaceAll('_', ' ')
}

function displayValue(value: unknown) {
  if (value == null || value === '') return '—'
  if (Array.isArray(value)) return value.join(' | ') || '—'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function changedFields(item: ApprovalItem) {
  const before = item.dados_anteriores ?? {}
  const after = item.dados_novos ?? {}
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
  return keys.filter((key) => JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null))
}

function formatDate(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR')
}

export function ApprovalsQueue() {
  const [items, setItems] = useState<ApprovalItem[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const result = await supabase.rpc('list_pending_approvals')
      if (result.error) throw result.error
      setItems((result.data ?? []) as ApprovalItem[])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar as aprovações pendentes.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const groupedCount = useMemo(() => new Set(items.map((item) => item.release_id)).size, [items])

  async function review(item: ApprovalItem, decision: 'aprovar' | 'rejeitar') {
    let reason: string | null = null
    if (decision === 'rejeitar') {
      const response = window.prompt(`Informe o motivo da rejeição de “${item.record_key}”. A Gestão verá que a alteração precisa ser corrigida.`)
      if (response == null) return
      reason = response.trim()
      if (!reason) return window.alert('O motivo da rejeição é obrigatório.')
    } else if (!window.confirm(`Aprovar esta alteração em ${DATASET_LABELS[item.dataset] ?? item.dataset}?`)) return

    setSavingId(item.change_id); setError(''); setMessage('')
    try {
      const result = await supabase.rpc('review_draft_change', { target_change_id: item.change_id, decision, reason })
      if (result.error) throw result.error
      setMessage(decision === 'aprovar' ? 'Alteração aprovada.' : 'Alteração rejeitada e devolvida para correção.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível concluir a revisão.')
    } finally {
      setSavingId(null)
    }
  }

  return (
    <section style={{ margin: '18px 0', padding: 18, border: '1px solid rgba(0,174,202,.24)', borderRadius: 18, background: 'linear-gradient(180deg, rgba(0,174,202,.035), #fff)', boxShadow: '0 9px 28px rgba(16,34,49,.045)' }}>
      <div className="pending-head"><div><p className="eyebrow">GOVERNANÇA</p><h2 style={{ marginBottom: 4 }}>Aprovações pendentes</h2><p className="muted" style={{ margin: 0 }}>{items.length} alteração(ões) em {groupedCount} release(s) aguardando sua revisão.</p></div><button className="secondary-button compact" disabled={loading} onClick={() => void load()}>{loading ? 'Atualizando...' : 'Atualizar fila'}</button></div>
      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}
      {!loading && items.length === 0 && <div className="empty-state" style={{ marginTop: 10 }}>Nenhuma alteração aguardando aprovação.</div>}

      <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
        {items.map((item) => {
          const fields = changedFields(item)
          const before = item.dados_anteriores ?? {}
          const after = item.dados_novos ?? {}
          return <article key={item.change_id} style={{ padding: 15, border: '1px solid var(--line)', borderRadius: 14, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div><div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><span className={`operation-badge ${item.operacao.toLowerCase()}`}>{item.operacao}</span><strong>{DATASET_LABELS[item.dataset] ?? item.dataset} · {item.record_key}</strong><span className="status-chip warning">Release {item.release_version}</span></div><p className="muted" style={{ margin: '7px 0 0', fontSize: '.72rem' }}>Solicitado por <strong>{item.requested_by}</strong> · {formatDate(item.submitted_at)}</p></div>
              <div style={{ display: 'flex', gap: 8 }}><button className="secondary-button compact" disabled={savingId === item.change_id} onClick={() => void review(item, 'rejeitar')}>Rejeitar</button><button className="primary-button compact" disabled={savingId === item.change_id} onClick={() => void review(item, 'aprovar')}>{savingId === item.change_id ? 'Processando...' : 'Aprovar'}</button></div>
            </div>

            <div style={{ marginTop: 13, display: 'grid', gap: 7 }}>
              {item.operacao === 'INSERT' && fields.map((field) => <div key={field} style={{ display: 'grid', gridTemplateColumns: 'minmax(150px,220px) 1fr', gap: 10, paddingTop: 7, borderTop: '1px solid #edf2f5' }}><strong style={{ fontSize: '.69rem' }}>{fieldLabel(field)}</strong><span style={{ fontSize: '.72rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{displayValue(after[field])}</span></div>)}
              {item.operacao === 'UPDATE' && fields.map((field) => <div key={field} style={{ display: 'grid', gridTemplateColumns: 'minmax(150px,220px) 1fr 20px 1fr', gap: 10, alignItems: 'start', paddingTop: 7, borderTop: '1px solid #edf2f5' }}><strong style={{ fontSize: '.69rem' }}>{fieldLabel(field)}</strong><span style={{ fontSize: '.72rem', whiteSpace: 'pre-wrap', color: '#647787', overflowWrap: 'anywhere' }}>{displayValue(before[field])}</span><strong aria-hidden="true">→</strong><span style={{ fontSize: '.72rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{displayValue(after[field])}</span></div>)}
              {item.operacao === 'DELETE' && <div className="notice error" style={{ margin: 0 }}>O registro será removido somente após aprovação e publicação da release.</div>}
            </div>
          </article>
        })}
      </div>
    </section>
  )
}
